import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { BalanceEntryRequest, CreateClaimRequest } from "../../../../api/ClientFileApi";
import { useClaimsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { ClaimStatusKey } from "../../../claimStatus";
import { ClaimFormValues } from "../../claim-form-fields/ClaimFormFields.types";
import { claimValidationSchema, createEmptyBalanceEntry } from "../../claim-form-fields/ClaimFormFields.utils";
import {
  toCollateralRequests,
  toExpectedLossPercent,
} from "../../claim-form-fields/components/collaterals/Collaterals.utils";
import { useClaimFormOptions } from "../../claim-form-fields/hooks/useClaimFormOptions";

export function useClaimCreation(clientId: string, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const claimsApi = useClaimsApi();
  const options = useClaimFormOptions();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (request: CreateClaimRequest) => claimsApi.createClaim({ clientId, createClaimRequest: request }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryAssets, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
      showSuccess(t("claims.create.successfullyCreated"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<ClaimFormValues>({
    initialValues: {
      creditorId: "",
      representativeId: "",
      caseNumber: "",
      legalBasis: "",
      disputedStatus: "",
      collateralStatus: "",
      expectedLossPercent: null,
      collaterals: [],
      claimEntirelyDisputed: "",
      recognizedAmount: null,
      srvRegistrationStatus: "",
      srvOrderNumber: null,
      notes: "",
      isCompleted: false,
      balanceEntries: [createEmptyBalanceEntry()],
    },
    validationSchema: claimValidationSchema(t, options.isSrvRegisteredEntryType),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const balanceEntries: BalanceEntryRequest[] = [];
      for (const e of values.balanceEntries) {
        if (e.date && e.amount !== null) {
          balanceEntries.push({ id: e.id ?? null, date: e.date, amount: e.amount, entryType: e.entryType });
        }
      }

      const request: CreateClaimRequest = {
        creditorId: values.creditorId,
        representativeId: values.representativeId || null,
        caseNumber: values.caseNumber || null,
        legalBasis: values.legalBasis,
        disputedStatus: values.disputedStatus,
        collateralStatus: values.collateralStatus,
        expectedLossPercent: toExpectedLossPercent(values),
        collaterals: toCollateralRequests(values),
        claimEntirelyDisputed:
          values.disputedStatus === ClaimStatusKey.Yes ? values.claimEntirelyDisputed || null : null,
        recognizedAmount:
          values.disputedStatus === ClaimStatusKey.Yes && values.claimEntirelyDisputed === ClaimStatusKey.No
            ? values.recognizedAmount
            : null,
        srvRegistrationStatus: values.srvRegistrationStatus || null,
        srvOrderNumber: values.srvOrderNumber,
        notes: values.notes || null,
        balanceEntries,
      };

      try {
        await createMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik, options };
}
